import {
  ArrowsClockwiseIcon,
  BarcodeIcon,
  DownloadSimpleIcon,
  ScrollIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import {
  Badge,
  type BadgeTone,
  Button,
  Cell,
  Column,
  Modal,
  Row,
  Select,
  TableFrame,
  TableHeader,
  Tooltip,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { exportSheetTypes } from '@utility/constants/types';
import { DateUtility } from '@utility/date';
import CatalogService from '@utility/services/catalog';
import { ToastMessage } from '@utility/toast';
import { type ReactNode, useEffect, useState } from 'react';
import { type ScmReport, useScmReports } from '../../hooks/use-scm-reports';

const SHEET_OPTIONS = [
  { value: exportSheetTypes.purchaseOrder, label: 'Purchase order sheet' },
  { value: exportSheetTypes.shipment, label: 'Shipment sheet' },
  { value: exportSheetTypes.inventory, label: 'Inventory sheet' },
];

const STATUS_TONES: Record<string, BadgeTone> = {
  pending: 'warning',
  completed: 'success',
  failed: 'danger',
};

const SHEET_ICONS: Record<string, ReactNode> = {
  [exportSheetTypes.purchaseOrder]: <ScrollIcon size={16} aria-hidden />,
  [exportSheetTypes.shipment]: <TruckIcon size={16} aria-hidden />,
  [exportSheetTypes.inventory]: <BarcodeIcon size={16} aria-hidden />,
};

function download(report: ScmReport) {
  if (!report.url) return;
  const fileName =
    report.url.split('?')[0].split('/').pop() ||
    `inventory-report-${DateUtility.formatDate(report.createdAt, 'MMM DD, YYYY - HHmmss')}.xlsx`;
  CommonUtility.downloadFile(report.url, fileName);
}

/**
 * Starts an SCM sheet export and lists the exported sheets. Open while
 * `sheet` is set; it opens on that sheet when it is one of the known kinds.
 */
export function ExportReportModal({
  sheet,
  onClose,
}: {
  sheet: string | null;
  onClose: () => void;
}) {
  const isOpen = !!sheet;
  const [feedType, setFeedType] = useState(exportSheetTypes.purchaseOrder);
  const [requesting, setRequesting] = useState(false);
  const reports = useScmReports(isOpen);

  useEffect(() => {
    if (sheet && Object.values(exportSheetTypes).includes(sheet)) {
      setFeedType(sheet);
    }
  }, [sheet]);

  const requestReport = async () => {
    try {
      setRequesting(true);
      await CatalogService.fetchSCMReport({ feedType });
      ToastMessage.success('Report fetch initiated successfully');
      reports.refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      size="xl"
      title="Export prediction report"
      footer={
        <Button variant="ghost" onPress={onClose}>
          Close
        </Button>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-4">
        <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
          <Select
            aria-label="Sheet type"
            value={feedType}
            onChange={setFeedType}
            options={SHEET_OPTIONS}
            className="sk:w-56"
          />
          <Button
            variant="primary"
            onPress={requestReport}
            isDisabled={requesting}
          >
            Fetch report
          </Button>
          <Tooltip content="Refresh">
            <Button
              variant="ghost"
              shape="circle"
              aria-label="Refresh"
              isDisabled={reports.loading}
              onPress={reports.refresh}
            >
              <ArrowsClockwiseIcon size={14} weight="bold" aria-hidden />
            </Button>
          </Tooltip>
        </div>
        <TableFrame
          aria-label="Exported reports"
          columnCount={4}
          isLoading={reports.loading}
          loadingRows={5}
          emptyMessage="No reports exported yet"
          pagination={{
            page: reports.page,
            pageSize: reports.limit,
            total: reports.reports.length ? reports.total : 0,
            onPageChange: reports.setPage,
            onPageSizeChange: reports.setLimit,
          }}
          header={
            <TableHeader>
              <Column id="date" isRowHeader>
                Exported
              </Column>
              <Column id="type">Type</Column>
              <Column id="status">Status</Column>
              <Column id="file" aria-label="File" className="sk:w-40">
                {''}
              </Column>
            </TableHeader>
          }
        >
          {reports.reports.map(report => (
            <Row key={report._id} id={report._id}>
              <Cell className="sk:whitespace-nowrap">
                {DateUtility.formatDate(report.createdAt)}
              </Cell>
              <Cell>
                <span className="sk:flex sk:items-center sk:gap-1.5">
                  {SHEET_ICONS[report.type]}
                  {CommonUtility.removeUnderscoreAndCapitalize(report.type)}
                </span>
              </Cell>
              <Cell>
                <Badge
                  tone={STATUS_TONES[report.status ?? ''] ?? 'neutral'}
                  className="sk:capitalize"
                >
                  {report.status}
                </Badge>
              </Cell>
              <Cell>
                <Button
                  variant="pill"
                  size="sm"
                  isDisabled={!report.url}
                  onPress={() => download(report)}
                >
                  <DownloadSimpleIcon size={12} weight="bold" aria-hidden />
                  Download
                </Button>
              </Cell>
            </Row>
          ))}
        </TableFrame>
      </div>
    </Modal>
  );
}
